/**
 * StatusChip — opinionated status pill.
 *
 * Where `Badge` is a generic primitive with consumer-supplied colors,
 * StatusChip bakes in the five canonical status semantics from the Tensaw
 * UI Standards: success, warning, danger, info, neutral. Colors flow from
 * `--tensaw-color-status-{variant}-bg/fg` so tenant theming re-themes every
 * chip without per-call coordination.
 *
 * Variant guidance (standards-v2.0 §1):
 *   success  → paid, accepted, current, confirmed
 *   warning  → pending, past due, in progress, recommended
 *   danger   → denied, overdue, no-show, rejected
 *   info     → submitted, scheduled, high confidence, in review
 *   neutral  → void, inactive, archived, overridden
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

export type StatusChipVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral';

export type StatusChipSize = 'sm' | 'md';

export interface StatusChipProps extends HTMLAttributes<HTMLSpanElement> {
  /** Semantic variant — picks the canonical status color pair. */
  variant: StatusChipVariant;
  /** Label text. */
  children: ReactNode;
  /** Size. Default `md`. */
  size?: StatusChipSize;
  /**
   * Uppercase the label. Default `true` to match the standards-v2.0 chip
   * vocabulary (RECOMMENDED, PAID, DENIED, …). Pass `false` for free-form
   * labels that should preserve case.
   */
  uppercase?: boolean;
}

/**
 * Canonical fallback hexes per standards-v2.0 §1. These match the values
 * emitted by ThemeProvider at runtime, so an untheme'd render still produces
 * the house style.
 */
const fallback: Record<StatusChipVariant, { bg: string; fg: string }> = {
  success: { bg: '#ECFDF5', fg: '#059669' },
  warning: { bg: '#FEF3C7', fg: '#D97706' },
  danger: { bg: '#FEE2E2', fg: '#DC2626' },
  info: { bg: '#DBEAFE', fg: '#2563EB' },
  neutral: { bg: '#F3F4F6', fg: '#6B7280' },
};

const sizing: Record<
  StatusChipSize,
  { fontSize: number; paddingV: number; paddingH: number; minHeight: number }
> = {
  sm: { fontSize: 11, paddingV: 2, paddingH: 6, minHeight: 18 },
  md: { fontSize: 12, paddingV: 4, paddingH: 8, minHeight: 22 },
};

export const StatusChip = forwardRef<HTMLSpanElement, StatusChipProps>(
  function StatusChip(
    {
      variant,
      children,
      size = 'md',
      uppercase = true,
      style,
      ...rest
    },
    ref,
  ) {
    const fb = fallback[variant];
    const sz = sizing[size];

    return (
      <span
        ref={ref}
        data-status-variant={variant}
        data-status-size={size}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          background: `var(--tensaw-color-status-${variant}-bg, ${fb.bg})`,
          color: `var(--tensaw-color-status-${variant}-fg, ${fb.fg})`,
          fontSize: sz.fontSize,
          fontWeight: 500,
          lineHeight: 1,
          padding: `${String(sz.paddingV)}px ${String(sz.paddingH)}px`,
          minHeight: sz.minHeight,
          borderRadius: 'var(--tensaw-radius-sm, 4px)',
          textTransform: uppercase ? 'uppercase' : 'none',
          letterSpacing: uppercase ? '0.04em' : 'normal',
          whiteSpace: 'nowrap',
          ...style,
        }}
        {...rest}
      >
        {children}
      </span>
    );
  },
);
StatusChip.displayName = 'StatusChip';
